import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Plus, Building2, MapPin, Search, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import usePrivilege from "../../hooks/usePrivilege";
import axiosInstance from "../../services/api/axios";
import { getMediaUrl, mapMediaItems } from "../../utils/media";
import { useUser } from "../../context/UserContext";
import { IconButton } from "../../components/Buttons";
import SearchInput from "../../components/SearchInput";
import { toast } from "react-toastify";
import usePagination from "../../hooks/usePagination";
import PaginationControls from "../../components/PaginationControls";

function ProjectList() {
  const navigate = useNavigate();
  const [mediaImages, setMediaImages] = useState({});
  const fetchingMediaRef = useRef(new Set()); // Use ref instead of state
  const { hasPrivilege } = usePrivilege();
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const { userInfo } = useUser();
  // eslint-disable-next-line no-unused-vars
  const [projectsLoaded, setProjectsLoaded] = useState(false);


  const role = userInfo?.persona;


  const CACHE_TTL_MS = 4 * 60 * 1000; // 4 minutes

  const getCachedImage = (projectId) => {
    const cached = localStorage.getItem(`media_${projectId}`);
    if (!cached) return null;
    try {
      const parsed = JSON.parse(cached);
      if (!parsed || !parsed.data || !parsed.cachedAt) return null;
      const isStale = Date.now() - parsed.cachedAt > CACHE_TTL_MS;
      return isStale ? null : parsed.data;
    } catch (error) {
      console.error("error while parsing cached data:", error);
      return null;
    }
  };
  
  const setCachedImage = (projectId, mediaData) => {
    try {
      const payload = {
        data: mediaData,
        cachedAt: Date.now(),
      };
      localStorage.setItem(`media_${projectId}`, JSON.stringify(payload));
    } catch (error) {
      console.error("Error storing in cache:", error);
    }
  };

  const fetchImages = useCallback(
    async (projectId) => {
      if (!projectId || fetchingMediaRef.current.has(projectId) || mediaImages[projectId])
        return;

      //Check if image available in cache
      const cachedImage = getCachedImage(projectId);
      if (cachedImage) {
        setMediaImages((prevMedia) => ({
          ...prevMedia,
          [projectId]: cachedImage,
        }));
        return;
      }

      // Add to fetching set
      fetchingMediaRef.current.add(projectId);
      
      try {
        const response = await axiosInstance.get("/projects/media", {
          params: {
            project_id: projectId,
            images: true,
          },
        });
        const mediaArray =
          response?.items || (Array.isArray(response) ? response : []);
        const [mediaData] = mapMediaItems(mediaArray, "Project Image");

        setMediaImages((prevMedia) => ({
          ...prevMedia,
          [projectId]: mediaData || null,
        }));
        if (mediaData) {
          setCachedImage(projectId, mediaData);
        }
      } catch (error) {
        console.error("Error fetching media images:", error);
        setError("Failed to fetch media images.");
        setMediaImages((prevMedia) => ({
          ...prevMedia,
          [projectId]: null,
        }));
      } finally {
        // Remove from fetching set
        fetchingMediaRef.current.delete(projectId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // No dependencies needed
  );

  // Create fetch function for projects with pagination
  const fetchProjectsBackend = useCallback(async ({ page, page_size, search }) => {
    try {
      const params = { page, page_size };
      if (search) params.search = search;

      let response;
      
      // Get customerId for all roles
      const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId || 
                        userInfo?.loginIdentifierDetails?.customerId;
      
      if (role === "SALES") {
        // SALES role: backend RBAC scoping on /projects already filters to
        // the projects this login has rbac_role_assignment rows for.
        const salesParams = {
          ...(search && { search }),
        };
        if (customerId) {
          salesParams.customerId = customerId;
        }

        const salesProjectsResponse = await axiosInstance.get(`/projects`, {
          params: salesParams,
        });

        if (salesProjectsResponse?.items && salesProjectsResponse.total !== undefined) {
          return salesProjectsResponse;
        } else if (Array.isArray(salesProjectsResponse)) {
          const startIndex = (page - 1) * page_size;
          const endIndex = startIndex + page_size;
          return {
            items: salesProjectsResponse.slice(startIndex, endIndex),
            total: salesProjectsResponse.length,
            page,
            page_size,
            total_pages: Math.ceil(salesProjectsResponse.length / page_size),
            has_next: endIndex < salesProjectsResponse.length,
            has_previous: page > 1,
          };
        } else {
          return { items: [], total: 0, page, page_size, total_pages: 0, has_next: false, has_previous: false };
        }
      } else if (role === "AGENT") {
        // For AGENT role, fetch from customers/relation endpoint (supports pagination)
        params.secondaryCustomerId = customerId;
        
        // Also include customerId as a separate parameter if needed
        if (customerId) {
          params.customerId = customerId;
        }
        
        response = await axiosInstance.get("/customers/relation", { params });
        
        // Handle both paginated and non-paginated responses
        const relationArray = response?.items || (Array.isArray(response) ? response : []);
        const projects = relationArray.map((item) => item.project);
        
        if (response?.items && response.total !== undefined) {
          return {
            ...response,
            items: projects
          };
        } else {
          // Fallback for non-paginated response
          const startIndex = (page - 1) * page_size;
          const endIndex = startIndex + page_size;
          const paginatedItems = projects.slice(startIndex, endIndex);
          return {
            items: paginatedItems,
            total: projects.length,
            page,
            page_size,
            total_pages: Math.ceil(projects.length / page_size),
            has_next: endIndex < projects.length,
            has_previous: page > 1
          };
        }
      } else {
        // For other roles, use /projects endpoint (supports pagination)
        if (customerId) {
          params.customerId = customerId;
        }
        
        response = await axiosInstance.get("/projects", { params });
        
        // Handle both paginated and non-paginated responses
        if (response?.items && response.total !== undefined) {
          return response;
        } else if (Array.isArray(response)) {
          // Fallback for non-paginated response
          const startIndex = (page - 1) * page_size;
          const endIndex = startIndex + page_size;
          const paginatedItems = response.slice(startIndex, endIndex);
          return {
            items: paginatedItems,
            total: response.length,
            page,
            page_size,
            total_pages: Math.ceil(response.length / page_size),
            has_next: endIndex < response.length,
            has_previous: page > 1
          };
        } else {
          return { items: [], total: 0, page, page_size, total_pages: 0, has_next: false, has_previous: false };
        }
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
      return { items: [], total: 0, page: 1, page_size: 24, total_pages: 0, has_next: false, has_previous: false };
    }
  }, [userInfo, role]);

  // Use the pagination hook with manual search
  const {
    data: projects,
    pagination,
    searchInput,
    loading: loadingProjects,
    error: paginationError,
    goToPage,
    changePageSize,
    handleSearchInputChange,
    triggerSearch,
    // eslint-disable-next-line no-unused-vars
    refetch
  } = usePagination(fetchProjectsBackend, 24, '', true, true);

  // Update error state from pagination
  useEffect(() => {
    if (paginationError) {
      setError("Failed to fetch projects.");
    }
  }, [paginationError]);

  useEffect(() => {
    if (projects && projects.length > 0) {
      setProjectsLoaded(true);
      const mediaPromises = projects.map((project) => {
        if (project?.projectId && !mediaImages[project.projectId] && !fetchingMediaRef.current.has(project.projectId)) {
          return fetchImages(project.projectId);
        }
        return null;
      });
      Promise.all(mediaPromises.filter(Boolean));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, fetchImages]); // mediaImages intentionally excluded to prevent infinite loops

  const projectList = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    return projects.map((project) => {
      if (!project) return null;
      const formattedAddress = project.address
        ? `${project.address.buildingNo} ${project.address.streetName1}${project.address.streetName2
          ? ", " + project.address.streetName2
          : ""
        }, 
               ${project.address.district}, ${project.address.city}, 
               ${project.address.state} ${project.address.pinCode}`
        : "Address not available";
      return {
        ...project,
        formattedAddress,
      };
    }).filter(Boolean);
  }, [projects]);

  const handleCreateProject = () => {
    navigate("/create-project");
  };

  const handleProjectClick = (projectId) => {
    if (projectId) {
      navigate(`/project-details/${projectId}`);
    } else {
      console.error("No projectId provided");
    }
  };

  const handleSearchChange = (value) => {
    handleSearchInputChange(value);
    // If search box is cleared, automatically trigger search to reset to initial state
    if (!value || value.trim() === '') {
      triggerSearch();
    }
  };

  useEffect(() => {
    const handleLogout = () => {
      // Clear localStorage for any keys that start with 'media_'
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith('media_')) {
          localStorage.removeItem(key);
        }
      }
    };

    window.addEventListener('logout', handleLogout);

    return () => {
      window.removeEventListener('logout', handleLogout);
    };
  }, []);

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      {/* Hero header — dark slate gradient, matches the rest of the app's
          section headers. Search and Create CTA live inside the band so the
          page reads as a single cohesive surface. */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 to-slate-600 p-5 lg:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-white tracking-tight">
              Projects
            </h1>
            <p className="text-xs text-slate-300 mt-0.5">
              {pagination?.total ?? 0} project{(pagination?.total ?? 0) === 1 ? "" : "s"}
              {searchInput ? ` matching "${searchInput}"` : " in your portfolio"}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") triggerSearch(); }}
                placeholder="Search projects…"
                disabled={loadingProjects}
                className="pl-9 pr-3 py-2 rounded-lg bg-white/10 text-white text-sm placeholder-slate-400 border border-white/10 focus:bg-white/20 focus:border-white/30 focus:outline-none w-full sm:w-72"
              />
            </div>
            {hasPrivilege("canEdit") && (
              <IconButton
                onClick={handleCreateProject}
                icon={<Plus className="mr-1.5 size-3.5" />}
                left
                className="!w-auto !px-3 !py-1.5 text-sm"
              >
                Add Project
              </IconButton>
            )}
          </div>
        </div>
        {/* Hidden SearchInput retained for the imperative triggerSearch() path
            from other call-sites — we drive search through the visible input
            above; the hidden one keeps any consumers that watch its props happy. */}
        <div className="hidden">
          <SearchInput
            value={searchInput}
            onChange={handleSearchChange}
            onSearch={triggerSearch}
            disabled={loadingProjects}
          />
        </div>
      </div>

      {loadingProjects ? (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {/* Skeleton cards — six placeholders with pulsing gradient. Looks
              richer than a centred spinner and gives the user a sense of
              what's about to land. */}
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white border border-slate-200 rounded-2xl overflow-hidden animate-pulse"
              style={{ minHeight: 320 }}
            >
              <div className="w-full h-40 bg-gradient-to-br from-slate-100 to-slate-200" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-full" />
                <div className="h-3 bg-slate-100 rounded w-2/3" />
                <div className="h-8 bg-slate-100 rounded w-full mt-3" />
              </div>
            </div>
          ))}
        </div>
      ) : projectList.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-flamingo-50 flex items-center justify-center">
            <Building2 size={28} className="text-flamingo-500" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-slate-900">
            {searchInput ? "No projects match your search" : "No projects yet"}
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {searchInput
              ? "Try a different name, developer, or city."
              : "Spin up your first project to start tracking phases, properties, and sales."}
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {projectList.map((project) => (
              <ProjectCard
                key={project?.projectId}
                project={project}
                imageData={mediaImages[project.projectId]}
                isLoadingImage={fetchingMediaRef.current.has(project.projectId)}
                onClick={() => handleProjectClick(project.projectId)}
              />
            ))}
          </div>

          {/* Pagination — pinned to the bottom of the page area via mt-auto so
              it sits at the bottom whether there's 1 card or 50. Plain text +
              chips, no card chrome. */}
          {!loadingProjects && pagination.total > 0 && (
            <div className="mt-auto pt-4 border-t border-slate-200">
              <PaginationControls
                pagination={pagination}
                onPageChange={goToPage}
                onPageSizeChange={changePageSize}
                itemName="projects"
                variant="list"
                showInfo={true}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * One project tile. Whole-card click navigates to details. Hover effects
 * (lift + image zoom + flamingo border) tell the user the card is actionable
 * without an explicit "View Details" button. Key stats (towers / units)
 * surface inline so the user can scan a portfolio at a glance.
 */
function ProjectCard({ project, imageData, isLoadingImage, onClick }) {
  const towers = project?.projectTowers || project?.towers || project?.project_towers;
  const units = project?.projectUnits || project?.units || project?.project_units;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col text-left bg-white border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:border-flamingo-200 focus:outline-none focus:ring-2 focus:ring-flamingo-300"
      style={{ minHeight: 320 }}
    >
      {/* Image with gradient overlay + stat chips floating on top */}
      <div className="relative overflow-hidden">
        {imageData ? (
          <img
            src={getMediaUrl(imageData)}
            alt={project?.projectName || "Project"}
            className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105"
            style={{ background: "#f3f4f6" }}
          />
        ) : isLoadingImage ? (
          <div className="w-full h-40 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-flamingo-500 border-t-transparent" />
          </div>
        ) : (
          <div className="w-full h-40 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <Building2 size={36} className="text-slate-300" />
          </div>
        )}
        {/* Slate gradient fades into the image at the bottom for readability
            when chips overlay it. */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        {(towers || units) && (
          <div className="absolute bottom-2 left-2 right-2 flex gap-1.5 flex-wrap">
            {towers ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-white/90 text-slate-700 backdrop-blur-sm">
                <Layers size={11} />
                {towers} {Number(towers) === 1 ? "tower" : "towers"}
              </span>
            ) : null}
            {units ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-flamingo-500/95 text-white backdrop-blur-sm">
                {units} {Number(units) === 1 ? "unit" : "units"}
              </span>
            ) : null}
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h2 className="font-semibold text-slate-900 text-base mb-1 truncate group-hover:text-flamingo-700 transition-colors">
          {project?.projectName || "Untitled project"}
        </h2>
        {project.developer && (
          <p className="text-xs text-slate-500 mb-2 truncate">
            by <span className="font-medium text-slate-700">{project.developer}</span>
          </p>
        )}
        <div className="flex items-start gap-1.5 text-xs text-slate-500 line-clamp-2 mt-auto">
          <MapPin size={12} className="shrink-0 mt-0.5 text-slate-400" />
          <span>{project.formattedAddress}</span>
        </div>
      </div>
    </button>
  );
}

export default ProjectList;